'use client';

import type { Key, ReactElement } from 'react';
import type { TooltipProps, TooltipTriggerComponentProps } from 'react-aria-components';
import { Tooltip as AriaTooltip, Focusable, TooltipTrigger } from 'react-aria-components';

import { useFusionTheme } from '../hooks';

import styles from './Tooltip.module.scss';

const DEFAULT_DELAYS = {
  OPEN: 500,
  CLOSE: 100
};

export interface Props
  extends Pick<
      TooltipProps,
      | 'isOpen'
      | 'onOpenChange'
      | 'className'
      | 'placement'
      | 'offset'
      | 'crossOffset'
      | 'shouldFlip'
    >,
    Pick<
      TooltipTriggerComponentProps,
      'isDisabled' | 'delay' | 'closeDelay' | 'trigger' | 'defaultOpen'
    > {
  children: [ReactElement, ReactElement | string];
  tooltipKey?: Key;
}

const Tooltip = ({
  children: [triggerContent, tooltipContent],
  trigger,
  isOpen,
  className,
  placement = 'bottom',
  isDisabled,
  offset,
  crossOffset,
  shouldFlip = true,
  delay = DEFAULT_DELAYS.OPEN,
  closeDelay = DEFAULT_DELAYS.CLOSE,
  defaultOpen,
  tooltipKey,
  onOpenChange
}: Props) => {
  const { addThemeClassName } = useFusionTheme();

  return (
    <TooltipTrigger
      trigger={trigger}
      isDisabled={isDisabled}
      delay={delay}
      closeDelay={closeDelay}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      defaultOpen={defaultOpen}
    >
      {/* @ts-expect-error - https://github.com/adobe/react-spectrum/issues/9062 */}
      <Focusable>{triggerContent}</Focusable>
      <AriaTooltip
        key={tooltipKey}
        shouldFlip={shouldFlip}
        offset={offset}
        crossOffset={crossOffset}
        className={addThemeClassName(styles.tooltip, className)}
        placement={placement}
      >
        {tooltipContent}
      </AriaTooltip>
    </TooltipTrigger>
  );
};
export default Tooltip;
